import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

import type { FavouriteButtonSize } from './FavouriteButton.types';

export const FAVOURITE_TRANSITION_MS = 300;

const PRESS_SCALE = 0.9;

export const SIZES: Record<FavouriteButtonSize, { button: number; icon: 'sm' | 'md' }> = {
  sm: { button: 32, icon: 'sm' },
  lg: { button: 44, icon: 'md' },
};

export const root = (size: FavouriteButtonSize) => {
  const { button } = SIZES[size];

  return css`
    align-items: center;
    appearance: none;
    background-color: var(${themeVariables.colors.surface.default});
    border: 1px solid var(${themeVariables.colors.stroke.default});
    border-radius: var(${themeVariables.borderRadius.full.mobile});
    box-sizing: border-box;
    color: var(${themeVariables.colors.content.interactive});
    cursor: pointer;
    display: inline-flex;
    height: ${button}px;
    justify-content: center;
    line-height: 0;
    padding: 0;
    width: ${button}px;

    &:hover {
      background-color: color-mix(
        in srgb,
        var(${themeVariables.colors.content.interactive}) var(${themeVariables.tint.hover}),
        var(${themeVariables.colors.surface.default}) calc(100% - var(${themeVariables.tint.hover}))
      );
    }

    &:active {
      transform: scale(${PRESS_SCALE});
    }

    &:focus-visible {
      outline: 2px solid var(${themeVariables.colors.stroke.focus});
      outline-offset: 2px;
    }
  `;
};

// Both hearts are stacked; visibility is toggled by the button's aria-pressed
// state so no prop interpolation or extra state attribute is needed.
export const heart = css`
  align-items: center;
  display: inline-flex;
  grid-area: 1 / 1;
  line-height: 0;
  transition: opacity ${FAVOURITE_TRANSITION_MS}ms ease, transform ${FAVOURITE_TRANSITION_MS}ms ease;

  & > svg {
    display: block;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const stack = css`
  display: grid;
  place-items: center;
`;

export const heartInactive = css`
  ${heart}
  opacity: 1;
  transform: scale(1);

  button[aria-pressed='true'] & {
    opacity: 0;
    transform: scale(0.8);
  }
`;

export const heartFull = css`
  ${heart}
  opacity: 0;
  transform: scale(0.8);

  button[aria-pressed='true'] & {
    opacity: 1;
    transform: scale(1);
  }
`;
